import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config as appConfig } from './config';
import { getFirebaseAuth } from '../firebase/config';
import { getCurrentLocale } from '../i18n';

export type ServiceType = 'portal' | 'kms' | 'ats';

const getBaseUrl = (service: ServiceType = 'portal'): string => {
  switch (service) {
    case 'kms':
      return appConfig.api.kmsUrl;
    case 'ats':
      return appConfig.api.atsUrl;
    default:
      return appConfig.api.baseUrl;
  }
};

/**
 * Create authenticated API client with workspace header support and auto token refresh
 */
export const createApiClient = (
  idToken?: string,
  service: ServiceType = 'portal'
): AxiosInstance => {
  // Shared token container to allow interceptor to read latest token
  const tokenRef = { current: idToken };

  const client = axios.create({
    baseURL: `${getBaseUrl(service)}/api/v1`,
    timeout: appConfig.api.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
    // Only apply paramsSerializer for ATS service (FastAPI needs ?id=a&id=b format)
    ...(service === 'ats' && {
      paramsSerializer: { indexes: null },
    }),
  });

  // Add request interceptor to include auth token and workspace header
  client.interceptors.request.use(async (config) => {
    // If no token provided, try to get fresh token from Firebase
    if (!tokenRef.current) {
      const auth = getFirebaseAuth();
      if (auth?.currentUser) {
        try {
          tokenRef.current = await auth.currentUser.getIdToken(true);
        } catch (error) {
          console.error('Failed to get fresh token:', error);
        }
      }
    }

    // Add auth token if available
    if (tokenRef.current) {
      config.headers.Authorization = `Bearer ${tokenRef.current}`;
    }

    // Add workspace header from localStorage
    const currentWorkspaceId = localStorage.getItem(appConfig.storage.WORKSPACE);
    if (currentWorkspaceId) {
      config.headers['X-Workspace-ID'] = currentWorkspaceId;
    }

    // Tell the backend which locale to translate messages into. The custom
    // X-Locale header is the primary signal (matches the LanguageSwitcher
    // exactly); Accept-Language stays for browsers/curl/legacy clients.
    // getCurrentLocale() is safe pre-init (falls back to DEFAULT_LOCALE).
    const activeLocale = getCurrentLocale();
    config.headers['X-Locale'] = activeLocale;
    config.headers['Accept-Language'] = activeLocale;

    return config;
  });

  // Add response interceptor to handle token expiration
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Check if error is 401 and we haven't retried yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        const auth = getFirebaseAuth();
        if (auth?.currentUser) {
          originalRequest._retry = true;

          try {
            // Force refresh the Firebase ID token
            const freshToken = await auth.currentUser.getIdToken(true);
            tokenRef.current = freshToken;

            // Update the authorization header with fresh token
            originalRequest.headers.Authorization = `Bearer ${freshToken}`;

            // Retry the original request with fresh token
            return client(originalRequest);
          } catch (refreshError) {
            // If token refresh fails, reject with original error
            console.error('Failed to refresh token:', refreshError);
            return Promise.reject(error);
          }
        }
      }

      if (error.response?.status === 403) {
        const data = error.response.data as
          | { code?: string; detail?: string; email?: string }
          | undefined;
        if (data?.code === 'BETA_ACCESS_DENIED') {
          window.dispatchEvent(
            new CustomEvent('beta:blocked', {
              detail: { detail: data.detail ?? '', email: data.email ?? '' },
            })
          );
        }
      }

      // For all other errors, reject as normal
      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Get current workspace ID from localStorage
 */
export const getCurrentWorkspaceId = (): string | null => {
  return localStorage.getItem(appConfig.storage.WORKSPACE);
};

/**
 * Set current workspace ID to localStorage
 */
export const setCurrentWorkspaceId = (workspaceId: string): void => {
  localStorage.setItem(appConfig.storage.WORKSPACE, workspaceId);
};

/**
 * Remove current workspace ID from localStorage
 */
export const clearCurrentWorkspaceId = (): void => {
  localStorage.removeItem(appConfig.storage.WORKSPACE);
};
